/**
 * TechHub agreements: renewal and notice dates (TechHub step 3, 2026-10-23).
 * Shared by the API, the daily reminder job and the screens so they always agree.
 * Calendar days only (clienthub-review.md, decision 6); business days come later.
 *
 *   ends_on         the end of the current or first term, as signed
 *   renewal auto    renews by renewal_months unless someone gives notice in time
 *   renewal manual  ends unless both sides sign a renewal
 *   notice_days     notice must be given this many days before the end
 * An auto-renewing agreement whose end has passed has rolled into its next term,
 * so its "current end" moves forward by whole renewal periods.
 */

export type Renewal = "none" | "auto" | "manual";
export interface AgreementDates {
  status: string;
  ends_on: string | null;
  renewal: Renewal;
  renewal_months: number | null;
  notice_days: number | null;
}
export interface Deadline {
  /** The end of the current term (after any automatic renewals). */
  current_end: string;
  /** The last day to give notice, when notice applies. */
  notice_by: string | null;
  notice_passed: boolean;
  /** The next date that needs action: notice_by while it's ahead, else the end. */
  next: string;
  next_kind: "notice" | "end";
  /** Days from today to `next` (negative once passed). */
  days: number;
  /** A non-renewing agreement past its end. */
  lapsed: boolean;
}

const ISO = /^\d{4}-\d{2}-\d{2}$/;
const parts = (iso: string) => iso.split("-").map(Number) as [number, number, number];
const lastDay = (y: number, m: number) => new Date(Date.UTC(y, m, 0)).getUTCDate();
const iso = (y: number, m: number, d: number) => `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;

/** Day number for comparisons (no time zone drift: dates are read at noon UTC). */
export const dayNo = (d: string) => Math.round(Date.parse(`${d.slice(0, 10)}T12:00:00Z`) / 864e5);
export const daysBetween = (from: string, to: string) => dayNo(to) - dayNo(from);

/** Add whole months. An end on the last day of a month stays on the last day (Feb 28 -> Feb 29 in a leap year -> Feb 28). */
export function addMonths(d: string, n: number): string {
  const [y, m, day] = parts(d.slice(0, 10));
  const endOfMonth = day === lastDay(y, m);
  const t = y * 12 + (m - 1) + n;
  const ny = Math.floor(t / 12), nm = (t % 12) + 1;
  const max = lastDay(ny, nm);
  return iso(ny, nm, endOfMonth ? max : Math.min(day, max));
}
export const addDays = (d: string, n: number) => new Date(Date.parse(`${d.slice(0, 10)}T12:00:00Z`) + n * 864e5).toISOString().slice(0, 10);

/** Today in Eastern time (the companies' time zone; the daily job runs at about 8 AM Eastern). */
export function todayEastern(now = new Date()): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone: "America/New_York", year: "numeric", month: "2-digit", day: "2-digit" }).format(now);
}

/** The end of the term in force today, rolling auto-renewals forward. */
export function currentEnd(a: AgreementDates, today: string): string | null {
  if (!a.ends_on || !ISO.test(a.ends_on.slice(0, 10))) return null;
  const end = a.ends_on.slice(0, 10);
  if (a.renewal !== "auto" || !a.renewal_months || a.status !== "active") return end;
  let k = 0, e = end;
  while (dayNo(e) < dayNo(today) && k < 600) { k++; e = addMonths(end, k * a.renewal_months); }
  return e;
}

/** Notice and end dates for an active agreement, or null when nothing is due (no end date, or not active). */
export function deadline(a: AgreementDates, today: string): Deadline | null {
  if (a.status !== "active") return null;
  const end = currentEnd(a, today);
  if (!end) return null;
  const notice = a.renewal !== "none" && a.notice_days ? addDays(end, -a.notice_days) : null;
  const noticeAhead = !!notice && dayNo(notice) >= dayNo(today);
  const next = noticeAhead ? notice! : end;
  return {
    current_end: end, notice_by: notice, notice_passed: !!notice && !noticeAhead,
    next, next_kind: noticeAhead ? "notice" : "end", days: daysBetween(today, next),
    lapsed: a.renewal !== "auto" && dayNo(end) < dayNo(today),
  };
}

/** Reminder days before a deadline (Bill's mockup: 90, 60, 30 and 7 days), plus the day itself. */
export const REMINDER_DAYS = [90, 60, 30, 7, 0];
